import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Brain, Loader2, ArrowUpCircle, ArrowDownCircle, MinusCircle, XCircle, PauseCircle, ShieldCheck, ShieldAlert, Link as LinkIcon } from "lucide-react";

const ICONS = {
  buy: <ArrowUpCircle className="w-4 h-4 text-[hsl(var(--gain))]" />,
  sell: <ArrowDownCircle className="w-4 h-4 text-[hsl(var(--loss))]" />,
  hold: <MinusCircle className="w-4 h-4 text-muted-foreground" />,
  skip: <XCircle className="w-4 h-4 text-muted-foreground" />,
  halt: <PauseCircle className="w-4 h-4 text-[hsl(var(--loss))]" />,
};

export default function BotPostmortem() {
  const { botId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verify, setVerify] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [bRes, dRes, vRes] = await Promise.all([
          api.get("/bots").catch(() => ({ data: [] })),
          api.get(`/bots/${botId}/decisions?limit=200`),
          api.get(`/bots/${botId}/verify`).catch(() => ({ data: null })),
        ]);
        const b = (Array.isArray(bRes.data) ? bRes.data : bRes.data?.bots || []).find((x) => x.id === botId);
        setBot(b);
        setRows(dRes.data.decisions || []);
        setVerify(vRes.data);
      } finally { setLoading(false); }
    };
    load();
  }, [botId]);

  const rerunVerify = async () => {
    setVerifyLoading(true);
    try {
      const { data } = await api.get(`/bots/${botId}/verify`);
      setVerify(data);
    } finally { setVerifyLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="postmortem-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate("/bots")} data-testid="pm-back" className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Bots
        </Button>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Postmortem</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2 mt-0.5">
            <Brain className="w-6 h-6" /> {bot?.name || "Bot"} — decisions
          </h1>
        </div>
      </div>

      {/* Snapshot chain verifier */}
      {verify && verify.chain_length > 0 && (
        <Card className={`p-5 rounded-2xl border-2 ${verify.valid ? "border-[hsl(var(--gain))]/40" : "border-[hsl(var(--loss))]/40"}`} data-testid="pm-verify-card">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              {verify.valid
                ? <ShieldCheck className="w-6 h-6 text-[hsl(var(--gain))]" data-testid="pm-verify-ok-icon" />
                : <ShieldAlert className="w-6 h-6 text-[hsl(var(--loss))]" data-testid="pm-verify-fail-icon" />}
              <div>
                <div className="font-medium" data-testid="pm-verify-status">
                  {verify.valid ? "Chain verified" : "Chain integrity failed"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {verify.chain_length} snapshots · SHA-256 chained · each link independently checkable
                </div>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={rerunVerify} disabled={verifyLoading} data-testid="pm-verify-rerun" className="rounded-full">
              {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
              <span className="ml-1">Re-verify</span>
            </Button>
          </div>
          <div className="flex flex-wrap gap-1" data-testid="pm-verify-dots">
            {verify.links.map((l, i) => (
              <div
                key={i}
                title={`${l.t}\n${l.return_pct >= 0 ? "+" : ""}${l.return_pct}% · equity $${l.equity}\nhash ${l.stored_hash.slice(0, 12)}…`}
                data-testid={`pm-verify-dot-${i}`}
                className={`w-2.5 h-2.5 rounded-full ${
                  l.ok ? "bg-[hsl(var(--gain))]" : "bg-[hsl(var(--loss))]"
                } hover:scale-150 transition-transform cursor-help`} />
            ))}
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground">
            Hover any dot for details. Each dot represents a signed snapshot: hash covers <span className="font-mono">sha256(previous_hash + equity + return_pct + orders_n + JWT_SECRET)</span>.
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-2xl" data-testid="pm-list">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading decisions…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center" data-testid="pm-empty">
            No decisions logged yet. As this bot runs, every tick's action + reasoning will appear here — including the alternatives it considered.
          </div>
        ) : (          <div className="space-y-2">
            {rows.map((d) => (
              <div key={d.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start p-3 rounded-lg border border-border/60 hover:bg-secondary/20 transition-colors"
                   data-testid={`pm-row-${d.id}`}>
                <div className="md:col-span-2 flex items-center gap-2">
                  {ICONS[d.action] || <MinusCircle className="w-4 h-4" />}
                  <div>
                    <div className="text-xs uppercase tracking-widest">{d.action}</div>
                    <div className="text-[10px] text-muted-foreground">{new Date(d.t).toLocaleString()}</div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Badge variant="secondary" className="rounded-full text-[10px]">{d.symbol}</Badge>
                  {d.price && <div className="text-xs font-mono-tab text-muted-foreground mt-1">@ ${Number(d.price).toFixed(2)}</div>}
                  {d.qty && <div className="text-[10px] text-muted-foreground">qty {Number(d.qty).toFixed(4)}</div>}
                </div>
                <div className="md:col-span-5 text-sm">{d.reason}</div>
                <div className="md:col-span-3 text-xs text-muted-foreground">
                  {d.alternative_considered && (
                    <div><span className="uppercase tracking-widest text-[9px]">alt considered</span><br/>{d.alternative_considered}</div>
                  )}
                  {d.indicators && (
                    <div className="mt-1 font-mono-tab text-[10px]">
                      {Object.entries(d.indicators).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="inline-block mr-2">{k}={typeof v === "number" ? v.toFixed(2) : String(v)}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
